import PropTypes from 'prop-types';
import { Modal, Form, Input, Button, message, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useChangePassword } from '../../Hooks/Index';

const { Text, Title } = Typography;

export default function ChangePasswordModal({ isOpen, onLogout }) {
  const [form] = Form.useForm();
  const { t } = useTranslation('forgetPassword');

  const changePasswordMutation = useChangePassword({
    onSuccess: () => {
      message.success(t('changePasswordSuccess'));
      setTimeout(() => {
        onLogout();
      }, 1500);
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.message || t('changePasswordFailed')
      );
    },
  });

  const handleSubmit = (values) => {
    changePasswordMutation.mutate({
      oldPassword: values.oldPassword,
      newPassword: values.newPassword,
      comparePassword: values.comparePassword,
    });
  };

  return (
    <Modal
      destroyOnHidden
      closable={false}
      footer={null}
      maskClosable={false}
      open={isOpen}
      title={<Title level={4} className="mb-0!">{t('changePasswordTitle')}</Title>}
    >
      <Text type="secondary">{t('changePasswordDescription')}</Text>
      <div className="mt-4">
        <Form
          className="mt-4"
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label={t('oldPassword')}
            name="oldPassword"
            rules={[
              { required: true, message: t('oldPasswordRequired') },
            ]}
          >
            <Input.Password placeholder={t('enterOldPassword')} />
          </Form.Item>

          <Form.Item
            hasFeedback
            label={t('newPassword')}
            name="newPassword"
            rules={[
              { required: true, message: t('newPasswordRequired') },
              {
                min: 8,
                message: t('passwordMinLength'),
              },
              {
                pattern: /[A-Z]/,
                message: t('passwordUppercase'),
              },
              {
                pattern: /[a-z]/,
                message: t('passwordLowercase'),
              },
              {
                pattern: /\d/,
                message: t('passwordNumber'),
              },
              {
                pattern: /[@$!%*?&]/,
                message: t('passwordSpecialChar'),
              },
            ]}
          >
            <Input.Password placeholder={t('enterNewPassword')} />
          </Form.Item>

          <Form.Item
            hasFeedback
            dependencies={['newPassword']}
            label={t('confirmPassword')}
            name="comparePassword"
            rules={[
              { required: true, message: t('confirmPasswordRequired') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('passwordMismatch')));
                },
              }),
            ]}
          >
            <Input.Password placeholder={t('enterConfirmPassword')} />
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button
              htmlType="submit"
              loading={changePasswordMutation.isPending}
              type="primary"
            >
              {t('changePasswordButton')}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
}

ChangePasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onLogout: PropTypes.func.isRequired,
};
